"use client";

import { useEffect, useRef, useState } from "react";
import {
  render,
  type RenderContext,
  type PlacementData,
} from "@/lib/garden/canvas-renderer";
import type { GardenZone, VerticalSupport } from "@/types/garden";
import plantsData from "@/data/plants.json";
import type { Plant } from "@/types/plants";

const plantsMap: Record<string, Plant> = {};
for (const p of plantsData as Plant[]) plantsMap[p.id] = p;

interface GardenViewerProps {
  gardenWidthCm: number;
  gardenHeightCm: number;
  zones: GardenZone[];
  placements: PlacementData[];
  supports: VerticalSupport[];
  className?: string;
}

const PIXELS_PER_CM = 2;

/**
 * Read-only garden plan canvas. Auto-fits the plan into the container and
 * keeps the marching-ants animation running. No interaction (no drag, no zoom).
 */
export function GardenViewer({
  gardenWidthCm,
  gardenHeightCm,
  zones,
  placements,
  supports,
  className,
}: GardenViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 800, height: 480 });

  const animTickRef = useRef(0);
  const rafRef = useRef<number>(0);

  const stateRef = useRef({
    size,
    gardenWidthCm,
    gardenHeightCm,
    zones,
    placements,
    supports,
  });
  stateRef.current = { size, gardenWidthCm, gardenHeightCm, zones, placements, supports };

  // Auto-resize to container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const dpr = window.devicePixelRatio || 1;
      setSize({
        width: Math.floor(width * dpr),
        height: Math.floor(height * dpr),
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Animation loop — re-uses the editor renderer for visual parity
  useEffect(() => {
    let running = true;
    function loop() {
      if (!running) return;
      animTickRef.current += 0.5;

      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          const s = stateRef.current;
          const dpr = window.devicePixelRatio || 1;
          const cssWidth = s.size.width / dpr;
          const cssHeight = s.size.height / dpr;

          // Fit garden in view (90% margin)
          const gardenPxW = s.gardenWidthCm * PIXELS_PER_CM;
          const gardenPxH = s.gardenHeightCm * PIXELS_PER_CM;
          const fitZoom = Math.min(
            cssWidth / gardenPxW,
            cssHeight / gardenPxH,
          ) * 0.92;
          const zoom = Math.max(0.1, Math.min(4, fitZoom));
          const renderedW = gardenPxW * zoom;
          const renderedH = gardenPxH * zoom;
          const offsetX = (cssWidth - renderedW) / 2;
          const offsetY = (cssHeight - renderedH) / 2;

          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

          const rc: RenderContext = {
            ctx,
            width: cssWidth,
            height: cssHeight,
            gardenWidthCm: s.gardenWidthCm,
            gardenHeightCm: s.gardenHeightCm,
            zoom,
            offsetX,
            offsetY,
            zones: s.zones,
            placements: s.placements,
            plants: plantsMap,
            selectedZoneId: null,
            selectedPlantId: null,
            hoveredZoneId: null,
            drawingRect: null,
            supports: s.supports,
            selectedSupportId: null,
            ghost: null,
            supportPreview: null,
            animTick: animTickRef.current,
          };

          render(rc);
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div ref={containerRef} className={className ?? "w-full h-80 relative"}>
      {/* The width/height attributes are the backing store, sized in device
          pixels by the observer above; the CSS size just fills the container.
          Deriving the CSS size from devicePixelRatio during render instead
          made the server (which has no such ratio) and the client disagree,
          and React reported a hydration mismatch on every public garden. */}
      <canvas
        ref={canvasRef}
        width={size.width}
        height={size.height}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}
